/**
 * RISK-LOG-1 — project Risk Log.
 * Score is probability × impact. Close and reopen go through the project
 * risk routes with a reason. Costs has no backend field, so that view is absent.
 */
import { useCallback, useEffect, useState } from "react";
import type { JSX } from "react";
import { useParams } from "react-router-dom";

import { RISK_BAND_TOKEN } from "./risk-score";
import {
  RISK_LOG_EMPTY,
  RISK_LOG_UNAVAILABLE,
  RISK_STATUSES,
  rowsForCell,
  type RiskLogData,
  type RiskLogRow,
  type RiskLogStatus,
} from "./risk-log-model";
import {
  closeProjectRisk,
  loadProjectRiskLog,
  reopenProjectRisk,
} from "./projectRisksLog.api";

type ViewId = "list" | "board" | "heat";
type GroupBy = "status" | "owner" | "severity";

const STATUS_LABEL: Record<RiskLogStatus, string> = {
  OPEN: "Open",
  MITIGATED: "Mitigated",
  ACCEPTED: "Accepted",
  CLOSED: "Closed",
};

function ownerLabel(ownerUserId: string | null): string {
  return ownerUserId ?? "No owner";
}

export default function ProjectRisksPage(): JSX.Element {
  const { projectId } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<RiskLogData | null>(null);
  const [view, setView] = useState<ViewId>("list");
  const [groupBy, setGroupBy] = useState<GroupBy>("status");
  const [cell, setCell] = useState<{ probability: number; impact: number } | null>(null);
  const [reasonFor, setReasonFor] = useState<string | null>(null);
  const [reason, setReason] = useState("");

  const load = useCallback(async (): Promise<void> => {
    if (!projectId) return;
    setLoading(true);
    try {
      setData(await loadProjectRiskLog(projectId));
    } catch {
      setData({ status: "unavailable" });
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const items = data?.status === "ready" ? data.items : [];
  const visible = cell ? rowsForCell(items, cell.probability, cell.impact) : items;

  async function submitReason(row: RiskLogRow): Promise<void> {
    if (!projectId) return;
    const why = reason.trim();
    if (!why) return;
    if (row.status === "CLOSED") {
      await reopenProjectRisk(projectId, row.id, why);
    } else {
      await closeProjectRisk(projectId, row.id, why);
    }
    setReason("");
    setReasonFor(null);
    await load();
  }

  return (
    <div className="p-6" data-testid="project-risks-root">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg font-semibold text-slate-900">Risk Log</h1>
        <div className="flex gap-2">
          {(["list", "board", "heat"] as const).map((id) => (
            <button
              key={id}
              type="button"
              data-testid={`risk-view-${id}`}
              className="rounded border border-slate-200 px-2 py-1 text-sm"
              onClick={() => setView(id)}
            >
              {id === "list" ? "List" : id === "board" ? "Board" : "Heat map"}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p data-testid="risk-log-loading">Loading</p>
      ) : !data || data.status === "unavailable" ? (
        <p data-testid="risk-log-unavailable">{RISK_LOG_UNAVAILABLE}</p>
      ) : data.status === "empty" ? (
        <p data-testid="risk-log-empty">{RISK_LOG_EMPTY}</p>
      ) : (
        <>
          <p className="mb-3 text-sm text-slate-600" data-testid="risk-log-total">
            {data.total}
          </p>
          {view === "board" ? (
            <label className="mb-3 block text-sm">
              Group by
              <select
                className="ml-2 rounded border border-slate-200 px-2 py-1"
                data-testid="risk-group-by"
                value={groupBy}
                onChange={(event) => setGroupBy(event.target.value as GroupBy)}
              >
                <option value="status">Status</option>
                <option value="owner">Owner</option>
                <option value="severity">Severity</option>
              </select>
            </label>
          ) : null}
          {view === "heat" ? (
            data.heat ? (
              <HeatMap
                heat={data.heat}
                onPick={(probability, impact) => {
                  setCell({ probability, impact });
                  setView("list");
                }}
              />
            ) : (
              <p data-testid="risk-heat-unavailable">{RISK_LOG_UNAVAILABLE}</p>
            )
          ) : null}
          {view === "board" ? (
            <Board rows={visible} groupBy={groupBy} />
          ) : view === "list" ? (
            <ul data-testid="risk-log-list">
              {visible.map((row) => (
                <li key={row.id} data-testid={`risk-row-${row.id}`} className="border-b border-slate-100 py-2">
                  <span>{row.title}</span>
                  <span className="ml-2" data-testid={`risk-score-${row.id}`}>
                    {row.score}
                  </span>
                  <span
                    className="ml-2 rounded px-1.5 py-0.5 text-xs text-white"
                    style={{ backgroundColor: RISK_BAND_TOKEN[row.band] }}
                    data-testid={`risk-band-${row.id}`}
                  >
                    {row.band}
                  </span>
                  <span className="ml-2 text-sm text-slate-600">{STATUS_LABEL[row.status]}</span>
                  <button
                    type="button"
                    className="ml-2 text-sm text-blue-700"
                    data-testid={`risk-transition-${row.id}`}
                    onClick={() => {
                      setReasonFor(row.id);
                      setReason("");
                    }}
                  >
                    {row.status === "CLOSED" ? "Reopen" : "Close"}
                  </button>
                  {reasonFor === row.id ? (
                    <form
                      className="mt-2 flex gap-2"
                      onSubmit={(event) => {
                        event.preventDefault();
                        void submitReason(row);
                      }}
                    >
                      <input
                        className="rounded border border-slate-200 px-2 py-1 text-sm"
                        data-testid={`risk-reason-${row.id}`}
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                        aria-label="Reason"
                      />
                      <button type="submit" className="text-sm" data-testid={`risk-reason-submit-${row.id}`}>
                        Save
                      </button>
                    </form>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
        </>
      )}
    </div>
  );
}

function HeatMap({
  heat,
  onPick,
}: {
  heat: number[][];
  onPick: (probability: number, impact: number) => void;
}): JSX.Element {
  return (
    <table className="mb-4 border-collapse" data-testid="risk-heat-map">
      <tbody>
        {[5, 4, 3, 2, 1].map((probability) => (
          <tr key={probability}>
            {[1, 2, 3, 4, 5].map((impact) => {
              const count = heat[probability - 1][impact - 1];
              return (
                <td key={impact} className="border border-slate-200 p-0">
                  <button
                    type="button"
                    className="h-10 w-10 text-sm"
                    data-testid={`risk-heat-${probability}-${impact}`}
                    onClick={() => onPick(probability, impact)}
                  >
                    {count}
                  </button>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Board({ rows, groupBy }: { rows: RiskLogRow[]; groupBy: GroupBy }): JSX.Element {
  const groups = new Map<string, RiskLogRow[]>();
  const keys =
    groupBy === "status"
      ? [...RISK_STATUSES]
      : groupBy === "severity"
        ? ["Low", "Medium", "High", "Critical"]
        : [];
  for (const key of keys) groups.set(key, []);
  for (const row of rows) {
    const key =
      groupBy === "status" ? row.status : groupBy === "owner" ? ownerLabel(row.ownerUserId) : row.band;
    const bucket = groups.get(key) ?? [];
    bucket.push(row);
    groups.set(key, bucket);
  }
  return (
    <div className="grid gap-3 md:grid-cols-2" data-testid="risk-board">
      {[...groups.entries()].map(([key, bucket]) => (
        <section key={key} data-testid={`risk-board-${key}`}>
          <h2 className="text-sm font-medium">
            {groupBy === "status" && (RISK_STATUSES as readonly string[]).includes(key)
              ? STATUS_LABEL[key as RiskLogStatus]
              : key}
          </h2>
          <ul>
            {bucket.map((row) => (
              <li key={row.id}>{row.title}</li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
